/**
 * Service de gestion des paiements fournisseurs.
 * Utilise authenticatedFetch pour les appels API serveur.
 */
import { authenticatedFetch } from '@/utils/authenticatedFetch';

// ─── Types ───────────────────────────────────────────────────────────────

export type PaymentStatus =
  | 'non_payee'
  | 'a_payer'
  | 'partiellement_payee'
  | 'payee'
  | 'domiciliation'
  | 'ignoree';

export interface PurchaseInvoiceWithPayment {
  id: string;
  supplier_name: string;
  invoice_number: string;
  invoice_date: string;
  total_ttc: number | null;
  date_echeance: string | null;
  communication_structuree: string | null;
  iban: string | null;
  bic: string | null;
  payment_status: PaymentStatus;
  marked_a_payer_at: string | null;
  marked_a_payer_by: string | null;
  marked_payee_at: string | null;
  marked_payee_by: string | null;
  file_path: string;
  extraction_status: string;
  created_at: string;
  montant_a_payer: number | null;
  total_paid: number;
  remaining: number;
  last_payment_date: string | null;
}

export interface PurchasePayment {
  id: string;
  purchase_invoice_id: string;
  amount: number;
  payment_date: string;
  reference: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface PaginatedPaymentResponse {
  data: PurchaseInvoiceWithPayment[];
  total: number;
  totalTtcSum: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `Erreur ${res.status}`);
  }
  return res.json();
}

// ─── API ─────────────────────────────────────────────────────────────────

export const purchasePaymentService = {
  /**
   * Liste paginée des factures avec informations de paiement.
   */
  async getInvoicesWithPayments(
    page: number,
    perPage: number,
    search?: string,
    statusFilter?: string,
    sortBy = 'invoice_date',
    sortAsc = false,
    includeTotal = false
  ): Promise<PaginatedPaymentResponse> {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(perPage),
      sort_by: sortBy,
      sort_asc: String(sortAsc),
    });
    if (search) params.set('search', search);
    if (statusFilter) params.set('payment_status', statusFilter);
    if (includeTotal) params.set('include_total', 'true');

    const res = await authenticatedFetch(`/api/purchase-invoices/with-payments?${params}`);
    return handleResponse<PaginatedPaymentResponse>(res);
  },

  /**
   * Marquer une facture comme "à payer".
   */
  async markAsAPayer(invoiceId: string, montantAPayer?: number): Promise<{ success: boolean }> {
    const res = await authenticatedFetch(`/api/purchase-invoices/${invoiceId}/mark-a-payer`, {
      method: 'POST',
      body: JSON.stringify({ montant_a_payer: montantAPayer ?? null }),
    });
    return handleResponse(res);
  },

  /**
   * Marquer une facture comme "payée" (solde complet).
   */
  async markAsPayee(invoiceId: string, paymentDate?: string): Promise<{ success: boolean }> {
    const res = await authenticatedFetch(`/api/purchase-invoices/${invoiceId}/mark-payee`, {
      method: 'POST',
      body: JSON.stringify({ payment_date: paymentDate }),
    });
    return handleResponse(res);
  },

  /**
   * Ajouter un paiement partiel.
   */
  async addPartialPayment(
    invoiceId: string,
    amount: number,
    paymentDate?: string,
    reference?: string,
    notes?: string
  ): Promise<{ success: boolean; payment_status: PaymentStatus; total_paid: number }> {
    const res = await authenticatedFetch(`/api/purchase-invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify({ amount, paymentDate, reference, notes }),
    });
    return handleResponse(res);
  },

  /**
   * Liste des paiements d'une facture.
   */
  async getPaymentsForInvoice(invoiceId: string): Promise<PurchasePayment[]> {
    const res = await authenticatedFetch(`/api/purchase-invoices/${invoiceId}/payments`);
    return handleResponse(res);
  },

  /**
   * Compteur de factures à payer (pour badge navbar).
   */
  async getAPayerCount(): Promise<number> {
    const res = await authenticatedFetch('/api/purchase-invoices/a-payer-count');
    const data = await handleResponse<{ count: number }>(res);
    return data.count;
  },

  /**
   * Lance le backfill Claude pour enrichir les factures sans montant/échéance/communication.
   */
  async backfillWithClaude(): Promise<{
    success: boolean;
    updated: number;
    claude_called: number;
    total: number;
    errors: { id: string; supplier: string; error: string }[];
  }> {
    const res = await authenticatedFetch('/api/purchase-invoices/backfill', {
      method: 'POST',
      body: JSON.stringify({ useClaude: true }),
    });
    return handleResponse(res);
  },

  /**
   * Marquer une facture comme ignorée (annulée, note de crédit, etc.).
   */
  async markAsIgnoree(invoiceId: string): Promise<{ success: boolean }> {
    const res = await authenticatedFetch(`/api/purchase-invoices/${invoiceId}/mark-ignoree`, {
      method: 'POST',
    });
    return handleResponse(res);
  },

  /**
   * Restaurer une facture ignorée.
   */
  async restoreFromIgnoree(invoiceId: string): Promise<{ success: boolean }> {
    const res = await authenticatedFetch(`/api/purchase-invoices/${invoiceId}/restore-ignoree`, {
      method: 'POST',
    });
    return handleResponse(res);
  },

  /**
   * Active/désactive la domiciliation pour un fournisseur.
   */
  async toggleDomiciliation(supplierName: string, enable: boolean): Promise<{ success: boolean }> {
    const res = await authenticatedFetch('/api/purchase-invoices/toggle-domiciliation', {
      method: 'POST',
      body: JSON.stringify({ supplier_name: supplierName, enable }),
    });
    return handleResponse(res);
  },

  /**
   * Finalise en batch les factures de fournisseurs de frais.
   */
  async finalizeExpenseInvoices(): Promise<{ finalized: number; total: number }> {
    const res = await authenticatedFetch('/api/purchase-invoices/finalize-expense', {
      method: 'POST',
    });
    return handleResponse(res);
  },

  /**
   * Génère la communication structurée de fallback.
   */
  generateCommunication(invoiceNumber: string, invoiceDate: string): string {
    const d = new Date(invoiceDate);
    const formatted = isNaN(d.getTime())
      ? invoiceDate
      : d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `Facture ${invoiceNumber} du ${formatted}`;
  },

  /**
   * Export CSV des factures filtrées.
   */
  exportCSV(invoices: PurchaseInvoiceWithPayment[]): void {
    const headers = [
      'Fournisseur',
      'N° Facture',
      'Montant TTC',
      'Déjà payé',
      'Reste à payer',
      'Échéance',
      'Communication',
      'IBAN',
      'BIC',
    ];
    const rows = invoices.map(inv => [
      inv.supplier_name,
      inv.invoice_number,
      inv.total_ttc != null ? inv.total_ttc.toFixed(2) : '',
      inv.total_paid > 0 ? inv.total_paid.toFixed(2) : '',
      inv.remaining > 0 ? inv.remaining.toFixed(2) : '',
      inv.date_echeance || '',
      inv.communication_structuree ||
        purchasePaymentService.generateCommunication(inv.invoice_number, inv.invoice_date),
      inv.iban || '',
      inv.bic || '',
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factures-a-payer-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
